import { msalInstance, config } from "utils/auth_azure";
import { GRAPH_URL } from "./calendario-urls";
import {
  CalendarEvent,
  CalendarViewResponse,
  NewEventPayload,
  UpdateEventPayload,
  UserProfile,
} from "../types";

// ========================================
// UTILS
// ========================================

const getTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Sao_Paulo";
  }
};

// ========================================
// AUTH - Token via MSAL
// ========================================

const CALENDAR_SCOPES = ["Calendars.ReadWrite", "User.Read"];

const getAccessToken = async (): Promise<string | null> => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    const silentRequest = {
      scopes: [...config.scopes, ...CALENDAR_SCOPES],
      account: accounts[0],
      forceRefresh: false,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch {
      // Se falhar silenciosamente, tenta via popup
      const response = await msalInstance.acquireTokenPopup({
        scopes: [...config.scopes, ...CALENDAR_SCOPES],
      });
      return response.accessToken;
    }
  } catch {
    return null;
  }
};

// ========================================
// FETCH HELPER
// ========================================

type GraphFetchOptions = RequestInit & {
  headers?: Record<string, string>;
};

const graphFetch = async <T = unknown>(
  path: string,
  options: GraphFetchOptions = {}
): Promise<T> => {
  const token = await getAccessToken();
  if (!token) throw new Error("Não autenticado com Microsoft Graph");

  const res = await fetch(`${GRAPH_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Prefer: `outlook.timezone="${getTimeZone()}"`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { error?: { message?: string } })?.error?.message ||
        `Graph API error ${res.status}`
    );
  }

  // DELETE retorna 204 No Content; accept/decline/tentativelyAccept retornam 200 com body vazio
  if (res.status === 204) return {} as T;

  const contentLength = res.headers.get("content-length");
  const contentType = res.headers.get("content-type") || "";
  if (contentLength === "0" || !contentType.includes("application/json")) {
    return {} as T;
  }

  return res.json() as Promise<T>;
};

// ========================================
// PERFIL DO USUÁRIO
// ========================================

const getUserProfile = async (): Promise<UserProfile> => {
  return graphFetch<UserProfile>(
    "/me?$select=displayName,mail,userPrincipalName,jobTitle,id"
  );
};

// ========================================
// FOTO DO USUÁRIO
// ========================================

const getUserPhoto = async (): Promise<string | null> => {
  try {
    const token = await getAccessToken();
    if (!token) return null;

    const res = await fetch(`${GRAPH_URL}/me/photo/$value`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) return null;

    const arrayBuffer = await res.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return `data:image/jpeg;base64,${base64}`;
  } catch {
    return null;
  }
};

// ========================================
// EVENTOS DO CALENDÁRIO - LEITURA
// ========================================

const getCalendarEvents = async (
  startDate: Date,
  endDate: Date
): Promise<CalendarEvent[]> => {
  const start = startDate.toISOString();
  const end = endDate.toISOString();

  const params = new URLSearchParams({
    startDateTime: start,
    endDateTime: end,
    $select:
      "id,subject,start,end,location,organizer,attendees,isAllDay,isCancelled,showAs,importance,bodyPreview,body,webLink,onlineMeetingUrl,onlineMeeting,recurrence,sensitivity,categories,isOnlineMeeting,responseStatus",
    $orderby: "start/dateTime",
    $top: "250",
  });

  const data = await graphFetch<CalendarViewResponse>(
    `/me/calendarView?${params}`
  );
  return data.value ?? [];
};

const getEventsForRange = async (
  monthsBefore = 1,
  monthsAfter = 2
): Promise<CalendarEvent[]> => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthsBefore, 1);
  const end = new Date(
    now.getFullYear(),
    now.getMonth() + monthsAfter + 1,
    0,
    23,
    59,
    59
  );
  return getCalendarEvents(start, end);
};

// ========================================
// EVENTOS DO CALENDÁRIO - CRUD
// ========================================

const createEvent = async (event: NewEventPayload): Promise<CalendarEvent> => {
  return graphFetch<CalendarEvent>("/me/events", {
    method: "POST",
    body: JSON.stringify(event),
  });
};

const updateEvent = async (
  eventId: string,
  event: UpdateEventPayload
): Promise<CalendarEvent> => {
  return graphFetch<CalendarEvent>(`/me/events/${eventId}`, {
    method: "PATCH",
    body: JSON.stringify(event),
  });
};

const deleteEvent = async (eventId: string): Promise<void> => {
  await graphFetch(`/me/events/${eventId}`, {
    method: "DELETE",
  });
};

const getEventById = async (eventId: string): Promise<CalendarEvent> => {
  return graphFetch<CalendarEvent>(
    `/me/events/${eventId}?$select=id,subject,start,end,location,organizer,attendees,isAllDay,isCancelled,showAs,importance,bodyPreview,body,webLink,onlineMeetingUrl,onlineMeeting,recurrence,sensitivity,categories,isOnlineMeeting,responseStatus`
  );
};

// ========================================
// RESPOSTA A EVENTOS (aceitar/recusar/tentativa)
// ========================================

const acceptEvent = async (
  eventId: string,
  comment?: string
): Promise<void> => {
  await graphFetch(`/me/events/${eventId}/accept`, {
    method: "POST",
    body: JSON.stringify({
      comment: comment || "",
      sendResponse: true,
    }),
  });
};

const declineEvent = async (
  eventId: string,
  comment?: string
): Promise<void> => {
  await graphFetch(`/me/events/${eventId}/decline`, {
    method: "POST",
    body: JSON.stringify({
      comment: comment || "",
      sendResponse: true,
    }),
  });
};

const tentativelyAcceptEvent = async (
  eventId: string,
  comment?: string
): Promise<void> => {
  await graphFetch(`/me/events/${eventId}/tentativelyAccept`, {
    method: "POST",
    body: JSON.stringify({
      comment: comment || "",
      sendResponse: true,
    }),
  });
};

// ========================================
// CANCELAR EVENTO (organizador)
// ========================================

const cancelEvent = async (
  eventId: string,
  comment?: string
): Promise<void> => {
  await graphFetch(`/me/events/${eventId}/cancel`, {
    method: "POST",
    body: JSON.stringify({
      comment: comment || "",
    }),
  });
};

// ========================================
// REUNIÃO ONLINE (Teams)
// ========================================

const createOnlineMeeting = async (
  subject: string,
  startDateTime: string,
  endDateTime: string
): Promise<CalendarEvent> => {
  return createEvent({
    subject,
    start: { dateTime: startDateTime, timeZone: getTimeZone() },
    end: { dateTime: endDateTime, timeZone: getTimeZone() },
    isOnlineMeeting: true,
    onlineMeetingProvider: "teamsForBusiness",
  });
};

// ========================================
// BUSCAR PESSOAS (para adicionar participantes)
// ========================================

interface PeopleSearchResult {
  value?: Array<{
    id?: string;
    displayName?: string;
    scoredEmailAddresses?: Array<{ address?: string }>;
    jobTitle?: string;
    department?: string;
  }>;
}

const searchPeople = async (
  query: string
): Promise<PeopleSearchResult["value"]> => {
  const params = new URLSearchParams({
    $search: `"${query}"`,
    $top: "10",
    $select: "id,displayName,scoredEmailAddresses,jobTitle,department",
  });

  const data = await graphFetch<PeopleSearchResult>(`/me/people?${params}`);
  return data.value ?? [];
};

// ========================================
// EXPORT
// ========================================

export const calendarioService = {
  // Auth
  getAccessToken,
  // Perfil
  getUserProfile,
  getUserPhoto,
  // Eventos - Leitura
  getCalendarEvents,
  getEventsForRange,
  getEventById,
  // Eventos - CRUD
  createEvent,
  updateEvent,
  deleteEvent,
  // Eventos - Respostas
  acceptEvent,
  declineEvent,
  tentativelyAcceptEvent,
  cancelEvent,
  // Online Meeting
  createOnlineMeeting,
  // Pessoas
  searchPeople,
};
